import { useMemo } from "react";
import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { BackButton } from "../../../../../common/components/back_button";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useAvaliacaoFornecedorCadastroViewModel } from "../../viewModels/avaliacao_fornecedor_cadastro_view_model";
import { FormRadioGroupInput } from "../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { FormDatePickerInput } from "../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../common/components/default_form_autocomplete";
import { CustomStyledDataGrid } from "./components/styled_data_grid";
import { GenerateAvaliacaoFornecedorCadastroColumnDefinition, GenerateProdutosServicosColumnDefinition } from "./columns";
import { LegendComponent } from "../../../../../common/components/legend_component";
import { LegendItemComponent } from "../../../../../common/components/legend_item_component";
import { LegendCircularIcon } from "../../../../../common/components/legend_circular_icon";
import { AvaliacaoOrdemCompraItem } from "../../../domain/models/avaliacao_ordem_compra";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { AvaliacaoOrdemCompraColetaModal } from "./components/avaliacao_ordem_compra_coleta_modal";
import { ProdutoServicoEntregue } from "../../../domain/models/entrega_ordem_compra_produto_servico";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { isValidId } from "../../../../../common/utils/utils";
import { DefaultFormNewSwitch } from "../../../../../common/components/default_form_new_switch";
import { DefaultFormDownloadInput } from "../../../../../common/components/default_form_download_input";
import { EntradaProdutoMultiModal } from "./components/entradaProduto_multi_modal";
import { CentroCustoDrawer } from "../../../../../common/components/drawers_api/drawers/centro_custo_drawer";

import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import BottomBar from "../../../../../common/components/bottom_bar";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * AvaliacaoFornecedorCadastro
 */

export const AvaliacaoFornecedorCadastro = () => {
  const viewModel = useAvaliacaoFornecedorCadastroViewModel();

  const produtosServicoColumnDefinition = useMemo(() => {
    return GenerateProdutosServicosColumnDefinition({
      canDelete: viewModel.avaliacaoOrdemCompra !== null &&
        viewModel.avaliacaoOrdemCompra.entregue !== true && viewModel.avaliacaoOrdemCompra.entregaParcial === true,
      onActionDeletar: viewModel.handleProdutoServicoDeleteOpenState,
      onActionDownload: viewModel.handleEntregaNotaFiscalDownload,
    });
  }, [
    viewModel.avaliacaoOrdemCompra,
    viewModel.handleProdutoServicoDeleteOpenState,
    viewModel.handleEntregaNotaFiscalDownload,
  ]);

  const columnDefinition = useMemo(() => {
    return GenerateAvaliacaoFornecedorCadastroColumnDefinition({
      onNotaChange: viewModel.handleAvaliacaoNotaChange,
    });
  }, [
    viewModel.handleAvaliacaoNotaChange,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              {viewModel.avaliacaoOrdemCompra?.entregue === true && (
                <PrimaryButton
                  type="button"
                  buttonColor={viewModel.avaliacaoOrdemCompra.produtosCadastradosEstoque ? "primary-green" : "yellow"}
                  onClick={() => {
                    if (viewModel.avaliacaoOrdemCompra?.produtosCadastradosEstoque) {
                      viewModel.handleVerEntradasEstoque();
                    } else if (viewModel.avaliacaoOrdemCompra) {
                      viewModel.handleEntradaMultiModalOpenState(true);
                    }
                  }}>
                  {viewModel.avaliacaoOrdemCompra.produtosCadastradosEstoque ? "Cadastrado no estoque" : "Cadastrar no estoque?"}
                </PrimaryButton>
              )}
              <BackButton
                onClick={viewModel.handleBack}
              />
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <form onSubmit={viewModel.handleAvaliacaoOrdemCompraSubmit}>
        <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Compras
            </Link>
            <Link underline="hover" color="inherit" component='span'>
              <LinkTo to='/OrdemCompras_entrega'>
                Avaliação de Fornecimento
              </LinkTo>
            </Link>
            <Typography className="text-primary-green">
              Avaliação do Fornecedor
            </Typography>
          </Breadcrumbs>

          <div className="flex flex-col gap-2">
            <div className="div flex justify-between items-center flex-col gap-2">
              <div className="flex gap-2 items-center w-full">
                <h1 className="text-3xl font-medium text-black dark:text-white">Avaliação do Fornecedor</h1>
                <RefreshButton onClick={viewModel.handleReload} />
              </div>

              <div className="w-full justify-end flex gap-2">
              </div>
            </div>
          </div>
          {
            viewModel.errorMessage !== undefined
              ? (
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.errorMessage}
                    onTryAgain={viewModel.handleReload} />
                </div>
              ) : (
                <>
                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                    <div className="flex flex-row items-center justify-between gap-2">
                      <div className="flex flex-col gap-0">
                        <h2 className="text-xl text-black dark:text-white font-semibold">Controle Geral de Avaliação do Fornecedor</h2>
                        {
                          (viewModel.avaliacaoOrdemCompra && viewModel.avaliacaoOrdemCompra.fornecedor) &&
                          <div className="w-full flex flex-col 2xl:flex-row 2xl:gap-8 2xl:justify-between">
                            <p className="text-base text-gray-600 dark:text-gray-200 font-normal ">
                              <span className="font-semibold">Fornecedor: </span>{viewModel.avaliacaoOrdemCompra.fornecedor} | Ordem de compra: {viewModel.avaliacaoOrdemCompra.nrOrdemCompra}
                            </p>
                            <p className="text-base text-gray-600 dark:text-gray-200 font-normal ">
                              <span className="font-semibold">Centro de Custo: </span>{viewModel.avaliacaoOrdemCompra.centroCusto}
                            </p>
                          </div>
                        }
                      </div>

                      <div className="flex flex-col lg:flex lg:flex-row gap-2">
                        {
                          viewModel.avaliacaoOrdemCompra &&
                          <div className="flex flex-col gap-1">
                            <p className="text-black dark:text-white text-[0.65rem]">
                              {
                                viewModel.avaliacaoOrdemCompra.nomeArquivoBoleto &&
                                "Boleto"
                              }
                              &nbsp;
                            </p>
                            <div className={`w-[17rem] h-[36px] ${viewModel.avaliacaoOrdemCompra.nomeArquivoBoleto ? 'bg-black/25 pl-4 rounded' : ''}`}>
                              <DefaultFormDownloadInput
                                name='arquivoBoleto'
                                nameLimparFlag='limparArquivoBoleto'
                                label='Anexar boleto'
                                maxFilenameSize={25}
                                saving={viewModel.saving}
                                filename={viewModel.avaliacaoOrdemCompra.nomeArquivoBoleto}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={viewModel.handleBoletoDownload}
                                onDeleteFile={viewModel.handleBoletoDelete}
                              />
                            </div>
                          </div>
                        }
                        {
                          viewModel.avaliacaoOrdemCompra &&
                          <div className="flex flex-col gap-1">
                            <p className="text-black dark:text-white text-[0.65rem]">
                              {
                                viewModel.avaliacaoOrdemCompra.nomeArquivo &&
                                "Nota Fiscal"
                              }
                              &nbsp;
                            </p>
                            <div className={`w-[17rem] h-[36px] ${viewModel.avaliacaoOrdemCompra.nomeArquivo ? 'bg-black/25 pl-4 rounded' : ''}`}>
                              <DefaultFormDownloadInput
                                name='arquivo'
                                nameLimparFlag='limparArquivo'
                                label='Anexar nota'
                                maxFilenameSize={25}
                                saving={viewModel.saving}
                                filename={viewModel.avaliacaoOrdemCompra.nomeArquivo}
                                reactHookForm={viewModel.hookForm}
                                onDownloadFile={viewModel.handleNotaFiscalDownload}
                                onDeleteFile={viewModel.handleNotaFiscalDelete}
                              />
                            </div>
                          </div>
                        }
                      </div>
                    </div>
                    <div className="w-full flex flex-col gap-2">
                      <div className="w-full grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-x-2 gap-y-2 items-end">
                        <div className="w-full grid grid-cols-1 col-span-2 lg:grid-cols-3 gap-y-2">
                          <div className="w-full col-span-2">
                            <FormRadioGroupInput
                              name='statusEntrega'
                              label='Material/Serviço Entregue'
                              required={true}
                              options={viewModel.statusEntregaOptions}
                              reactHookForm={viewModel.hookForm}
                              onValueChange={viewModel.handleStatusEntregaChange}
                            />
                          </div>

                          <DefaultFormNewSwitch
                            name='statusNfEntrega'
                            label='NF Entregue'
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>

                        <div className="flex flex-col mb-2">
                          <FormDatePickerInput
                            name='data'
                            label='Data'
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>
                      </div>

                      <div className="w-full grid grid-cols-1 lg:grid-cols-2 gap-x-2 gap-y-4">
                        <DefaultFormAutocomplete
                          name='avaliadoPor'
                          label='Avaliado Por'
                          getOptionDisabled={x => !x.canBeSelected}
                          options={viewModel.funcionariosDropdown}
                          getOptionKey={x => x.idFuncionario}
                          getOptionLabel={x => x.nome}
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='nroNotaFiscal'
                          label='Nro Nota Fiscal'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>
                  </div>

                  {
                    (viewModel.avaliacaoOrdemCompra &&
                      (viewModel.statusEntrega === 1 || viewModel.statusEntrega === 2)) &&
                    <>
                      <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white">
                        <div className="w-full flex flex-row justify-between">
                          <h2 className="text-xl text-black dark:text-white font-normal">
                            Informações do Produto/Serviço
                          </h2>
                          <div className="flex flex-row items-center gap-2 border-b border-border-light-mode dark:border-border-dark-mode mt-2">
                            {
                              (viewModel.avaliacaoOrdemCompra && viewModel.statusEntrega === 2 &&
                                viewModel.produtosServicosParaEntrega && viewModel.produtosServicosParaEntrega.length > 0) &&
                              <PrimaryButton
                                buttonColor="primary-green"
                                startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                                onClick={() => viewModel.handleAvaliacaoOrdemCompraColetaOpenState(true)}>
                                Nova Entrega Parcial
                              </PrimaryButton>
                            }
                          </div>
                        </div>

                        <div className="w-full flex flex-col gap-2 mt-4">
                          {/*
                          <span className="text-lg font-bold text-black dark:text-white">Pesquisar</span>
                          <DefaultMultiAutocomplete
                            label='Produto/Serviço'
                            options={viewModel.produtosServicosDropDown}
                            getOptionKey={x => x.idProdutoServico}
                            getOptionLabel={x => x.descricao}
                            value={viewModel.selectedProdutosServicosQuery}
                            onChange={viewModel.handleProdutosServicosQueryChange}
                          />
                          */}

                          <div className="w-full h-[1rem]"></div>
                          <StyledDataGrid
                            tableName="avaliacao_fornecedor_cadastro_produtos_servicos"
                            disableRowSelectionOnClick={true}
                            disableColumnMenu={false}
                            textSize="pequeno"
                            autoHeight={true}
                            getRowHeight={() => 'auto'}
                            getRowId={(row: ProdutoServicoEntregue) => row.idProdutoServicoEntregue}
                            columns={produtosServicoColumnDefinition}
                            loading={viewModel.loading || viewModel.saving}
                            rows={viewModel.entregaProdutosServicos}
                            disableColumnSorting={true}
                            disableColumnResize={true}
                            showToolbar={false}
                          />

                          <TablePagination
                            component="div"
                            count={viewModel.pagination.totalItems}
                            page={viewModel.pagination.page - 1}
                            onPageChange={(_, p) => viewModel.handlePageChange(p)}
                            rowsPerPageOptions={[10, 25, 50, 100]}
                            rowsPerPage={viewModel.pagination.rowsPerPage}
                            onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                          />
                        </div>
                      </div>
                    </>
                  }

                  <div className="w-full flex flex-col gap-2 p-4 bg-light-mode-2 dark:bg-dark-mode-2 rounded border border-border-light-mode dark:border-border-dark-mode text-black dark:text-white mb-8">
                    <div className="flex flex-row items-center gap-2 border-b border-border-light-mode dark:border-border-dark-mode">
                      <h2 className="text-xl text-black dark:text-white font-normal">
                        Critérios de Avaliação
                      </h2>
                    </div>

                    <div className="w-full flex flex-col gap-2">
                      <Stack>
                        <div className="w-full flex flex-row justify-between mt-4">
                          <div className="flex flex-col flex-wrap justify-end">
                            <LegendComponent>
                              <LegendItemComponent
                                icon={<LegendCircularIcon color='red' title='1' />}
                                label='Não Atendeu'
                              />

                              <LegendItemComponent
                                icon={<LegendCircularIcon color='yellow' title='2' />}
                                label='Atendeu Parcialmente'
                              />

                              <LegendItemComponent
                                icon={<LegendCircularIcon color='green' title='3' />}
                                label='Atendeu Plenamente'
                              />
                            </LegendComponent>
                          </div>

                          <div className="flex flex-row gap-4 text-black dark:text-white">
                            <span>NOTA MÉDIA:</span>
                            <span className="font-bold">
                              {
                                viewModel.notaMedia.toFixed(2).replaceAll('.', ',')
                              }
                            </span>
                          </div>
                        </div>

                        <CustomStyledDataGrid
                          tableName="avaliacao_fornecedor_cadastro"
                          disableRowSelectionOnClick={true}
                          disableColumnMenu={false}
                          autoHeight={true}
                          getRowId={(row: AvaliacaoOrdemCompraItem) => row.idCriterioAvaliacaoFornecedor}
                          columns={columnDefinition}
                          loading={viewModel.loading || viewModel.saving}
                          rows={viewModel.criterioItems}
                          disableColumnSorting={true}
                          disableColumnResize={true}
                          showToolbar={false}
                          sortModel={[{ field: 'ordem', sort: 'asc' }]}
                        />

                        <div className="w-full flex flex-row justify-between">
                          <div>
                            {viewModel.hookForm.formState.errors.root &&
                              <span className="text-red-500 text-sm">
                                {viewModel.hookForm.formState.errors.root.message}
                              </span>
                            }
                          </div>
                        </div>
                      </Stack>

                      <div className="w-full flex flex-col gap-1 mt-4 justify-center">
                        <div className="flex flow-row gap-2">
                          <span className="text-black dark:text-white font-bold">
                            Observações
                          </span>
                          <span className="text-red-500 text-sm mt-[0.15rem]">
                            (Em caso de nota 1 ou 2, inserir o motivo da nota e qual será a tratativa.)
                          </span>
                        </div>

                        <DefaultFormInput
                          name='observacao'
                          label=''
                          multiline={true}
                          rows={7}
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>
                  </div>
                </>
              )
          }
        </div>

        {/**
          * BottomBar
          */}

        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isValidId(viewModel.idOrdemCompra) &&
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                  <span className="text-xs">
                    Historico de alterações
                  </span>
                </PrimaryButton>
              }
            </div>

            <div className="flex flex-row gap-4">
              <button
                type="button"
                className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold text-sm text-black dark:text-white"
                onClick={viewModel.handleBack}>
                Cancelar
              </button>
              <div className="w-[130px]">
                {viewModel.saving
                  ? (
                    <PrimaryButton>
                      Salvando...
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      type="submit"
                      disabled={viewModel.errorMessage !== undefined}
                      startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                      Salvar
                    </PrimaryButton>
                  )}
              </div>
            </div>
          </div>
        </BottomBar>
      </form>

      {/**
        * Modals
        */}

      <AvaliacaoOrdemCompraColetaModal
        open={viewModel.avaliacaoOrdemCompraColetaModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        produtosServicosParaEntrega={viewModel.produtosServicosParaEntrega}
        onClose={() => viewModel.handleAvaliacaoOrdemCompraColetaOpenState(false)}
        onSubmit={viewModel.handleAvaliacaoOrdemCompraColetaSubmit}
      />

      <EntradaProdutoMultiModal
        open={viewModel.entradaMultiModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        avaliacaoOrdemCompra={viewModel.avaliacaoOrdemCompra}
        selectedEntradaProduto={viewModel.selectedEntradaProduto}
        entradaProduto={viewModel.entradaProdutosEstoque}
        centroCustoLista={viewModel.centroCustoEstoqueLista}
        onCreateCentroCusto={viewModel.handleCreateCentroCustoEstoque}
        onClose={() => viewModel.handleEntradaMultiModalOpenState(false)}
        onSubmit={viewModel.handleEntradaProdutoMultiSubmit}
        onOpenChangeHistory={viewModel.handleEntradaProdutoOpenChangeHistory}
      />

      <CentroCustoDrawer
        saving={viewModel.saving}
        state={viewModel.centroCustoDrawerState}
      />

      <DeleteDialog
        open={viewModel.produtoServicoDeleteModal}
        onClose={() => viewModel.handleProdutoServicoDeleteOpenState(null, false)}
        title={`Você realmente deseja excluir este item?`}
        onDelete={viewModel.handleProdutoServicoDeleteSubmit}
      />

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />
    </>
  );
}
